import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import VideoExcelDownloadBtn from "components/project/VideoExcelDownloadBtn";
import PdfDownloadBtn, { REPORT_PRINT_PDF_LAYOUT } from "components/project/PdfDownloadBtn";
import HmlDownloadBtn from "components/project/HmlDownloadBtn";
import Spinner from "components/Spinner";
import { getImageBase64 } from "lib/api/getImageBase64";
import {
  getReportPanelScalePref,
  normalizeReportPanelScalePref,
  REPORT_PANEL_SCALE_PREF,
  setReportPanelScalePref,
} from "lib/utils/reportPanelScalePref";
import { useAppStore } from "lib/store/useAppStore";
import {
  getFusionResultTone,
  getVideoFusionLabelsByLocale,
  normalizeFusionResultCode,
} from "lib/utils/fusionResult";
import Ud1000VideoEditModal from "./components/Ud1000VideoEditModal";
import Ud1000VideoSummaryTable from "./components/Ud1000VideoSummaryTable";

const EMPTY_REPORT_IMAGES = {};

export default function VideoReportPanel({
  file,
  isReadOnly,
  form,
  setForm,
  setIsReadOnly,
  t,
  reportActionSlot = null,
  onSave,
  isSaving = false,
  hasChanges = false,
}) {
  const { i18n } = useTranslation();
  const user = useAppStore((s) => s.user || null);
  const canRenderHwp = (i18n.resolvedLanguage ?? i18n.language ?? "")
    .toLowerCase()
    .startsWith("ko");
  const tableRef = useRef();
  const ultrasonicRef1 = useRef();
  const ultrasonicRef2 = useRef();
  const fftRef1 = useRef();
  const fftRef2 = useRef();
  const skipNextReportScaleSaveRef = useRef(true);

  const fusionLabels = getVideoFusionLabelsByLocale(i18n);
  const [reportScale, setReportScale] = useState(
    REPORT_PANEL_SCALE_PREF.DEFAULT_SCALE,
  );
  const [reportImages, setReportImages] = useState(() => ({
    loading: Boolean(file?.upload_id),
    images: EMPTY_REPORT_IMAGES,
  }));
  const { loading, images: imgTemp } = reportImages;
  const resolvedUserId = user?.id;

  const draftData = isReadOnly ? { ...(file ?? {}), ...(form ?? {}) } : file;

  function normalizeFusionStatusCode(value) {
    return normalizeFusionResultCode(value, null);
  }

  function toFusionLabel(value) {
    const code = normalizeFusionStatusCode(value);
    if (code === null) return "";
    return fusionLabels[code] ?? "";
  }

  function handleFusionResultChange(event) {
    const nextCode = Number(event.target.value);
    setForm((prev) => ({
      ...(prev ?? {}),
      fusion_result: Number.isFinite(nextCode) ? nextCode : 0,
      fusion_result_code: Number.isFinite(nextCode) ? nextCode : 0,
    }));
  }

  function handleChange(event) {
    const { id, value } = event.target;
    setForm((prev) => ({
      ...(prev ?? {}),
      [id]: value,
    }));
  }

  useEffect(() => {
    const uploadId = file?.upload_id;

    if (!uploadId) {
      setReportImages((prev) => {
        if (!prev.loading && prev.images === EMPTY_REPORT_IMAGES) {
          return prev;
        }

        return {
          loading: false,
          images: EMPTY_REPORT_IMAGES,
        };
      });
      return;
    }

    let cancelled = false;

    setReportImages((prev) => {
      if (prev.loading && prev.images === EMPTY_REPORT_IMAGES) {
        return prev;
      }

      return {
        loading: true,
        images: EMPTY_REPORT_IMAGES,
      };
    });

    (async () => {
      const { success, data, message } = await getImageBase64(
        uploadId,
        "video_report",
      );

      if (cancelled) return;

      if (!success) {
        setReportImages({
          loading: false,
          images: EMPTY_REPORT_IMAGES,
        });
        console.warn("[Ud1000ReportTypeVideo] Base64 load failed:", message);
        return;
      }

      setReportImages({
        loading: false,
        images: { ...data },
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [file?.upload_id]);

  useEffect(() => {
    if (!isReadOnly) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsReadOnly(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    document.body.classList.add("reportEditModalOpen");

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.classList.remove("reportEditModalOpen");
    };
  }, [isReadOnly, setIsReadOnly]);

  useEffect(() => {
    skipNextReportScaleSaveRef.current = true;
    setReportScale(getReportPanelScalePref(resolvedUserId));
  }, [resolvedUserId]);

  useEffect(() => {
    if (skipNextReportScaleSaveRef.current) {
      skipNextReportScaleSaveRef.current = false;
      return;
    }

    if (!resolvedUserId) {
      return;
    }

    setReportPanelScalePref(resolvedUserId, reportScale);
  }, [reportScale, resolvedUserId]);

  const panelFusionCode = normalizeFusionStatusCode(
    draftData?.fusion_result_code ?? draftData?.fusion_result,
  );
  const panelFusionTone = getFusionResultTone(panelFusionCode);

  if (loading) {
    return <Spinner />;
  }

  return (
    <div
      className={`reportPanel ${
        panelFusionTone === "warning"
          ? "active1"
          : panelFusionTone === "abnormal"
            ? "active2"
            : panelFusionTone === "recheck"
              ? "active3"
              : ""
        }`}
    >
      <div className="reportPanelActionBar">
        <div className="reportPanelDownloadActions">
          <PdfDownloadBtn
            t={t("videoFileIndex.buttons.PdfDownload")}
            ref={tableRef}
            file_name={file?.file_name}
            layout={REPORT_PRINT_PDF_LAYOUT.single}
          />
          <VideoExcelDownloadBtn t={t} form={draftData} images={imgTemp} />
          {canRenderHwp ? (
            <HmlDownloadBtn
              upload_id={file?.upload_id}
              file_name={file?.clean_file_name}
              file_type={file?.file_type}
              t={t("videoFileIndex.buttons.HmlDownload")}
            />
          ) : null}
        </div>
        <div className="reportPanelEditActions">
          {renderReportScaleControls()}
          {reportActionSlot}
        </div>
      </div>

      <div className="reportPanelContent">
        <Ud1000VideoSummaryTable
          tableRef={tableRef}
          t={t}
          draftData={draftData}
          imgTemp={imgTemp}
          previewScale={reportScale}
          toFusionLabel={toFusionLabel}
          ultrasonicRef1={ultrasonicRef1}
          ultrasonicRef2={ultrasonicRef2}
          fftRef1={fftRef1}
          fftRef2={fftRef2}
        />
      </div>
      {isReadOnly ? (
        <Ud1000VideoEditModal
          t={t}
          form={form}
          handleChange={handleChange}
          handleFusionResultChange={handleFusionResultChange}
          toFusionCode={normalizeFusionStatusCode}
          fusionLabels={fusionLabels}
          setIsReadOnly={setIsReadOnly}
          onSave={onSave}
          isSaving={isSaving}
          hasChanges={hasChanges}
        />
      ) : null}
    </div>
  );

  function handleAdjustReportScale(direction) {
    const delta = direction * REPORT_PANEL_SCALE_PREF.STEP;
    setReportScale((prev) => normalizeReportPanelScalePref(prev + delta));
  }

  function renderReportScaleControls() {
    const isDecreaseDisabled =
      reportScale <= REPORT_PANEL_SCALE_PREF.MIN_SCALE + 0.001;
    const isIncreaseDisabled =
      reportScale >= REPORT_PANEL_SCALE_PREF.MAX_SCALE - 0.001;

    return (
      <div className="reportPanelScaleControls">
        <span className="reportPanelScaleLabel">
          {t("videoFileIndex.buttons.reportScale", {
            defaultValue: "Size",
          })}
        </span>
        <button
          type="button"
          className="reportPanelScaleButton"
          onClick={() => handleAdjustReportScale(-1)}
          disabled={isDecreaseDisabled}
          aria-label={t("videoFileIndex.buttons.reportScaleDown", {
            defaultValue: "Decrease report size",
          })}
        >
          -
        </button>
        <span className="reportPanelScaleValue">
          {Math.round(reportScale * 100)}%
        </span>
        <button
          type="button"
          className="reportPanelScaleButton"
          onClick={() => handleAdjustReportScale(1)}
          disabled={isIncreaseDisabled}
          aria-label={t("videoFileIndex.buttons.reportScaleUp", {
            defaultValue: "Increase report size",
          })}
        >
          +
        </button>
      </div>
    );
  }
}
